import { Component, Inject, OnInit, OnDestroy, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {FormBuilder,FormGroup,Validators,AbstractControl,ValidatorFn} from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { RecettesService } from 'src/app/services/recettes/recettes.service';
import { SnackbarService } from 'src/app/services/snackbar/snackbar.service';
import { Subscription, of, from, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap, catchError, takeUntil, startWith, filter, map } from 'rxjs/operators';
import { FactureCreate } from 'src/app/utils/interfaces/new/Facture';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { ResultatPatient } from 'src/app/utils/interfaces/new/resultat-patient.interface';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { PatientService } from 'src/app/services/patient/patient.service';
import { PerimetersService } from 'src/app/services/perimeters/perimeters.service';
import { RessourceService } from 'src/app/services/ressource/ressource.service';

export interface AddFactureHandData {
  profils?: Array<{ nom: string; prenom: string; id: string }> ;
  defaultProfilId?: string;
  prefillPatient?: { id: string; display: string };
  defaultDate?: Date;
  montantSuggestion?: number;
}

@Component({
  selector: 'app-add-facture-hand',
  templateUrl: './add-facture-hand.component.html',
  styleUrls: ['./add-facture-hand.component.scss']
})

export class AddFactureHandComponent implements OnInit, OnDestroy {
  @ViewChild(MatAutocompleteTrigger, { static: false })
    autoTrigger: MatAutocompleteTrigger;

    form: FormGroup;
    loading = false;
    today = new Date();
    // Autocomplete patients
    patientsList: any[] = [];
    patientSelected: any | null = null;
    patientId: string | null = null;
    SearchPatientNom: string | null = null;
    // UI options
    typesFse = [
      { value: 'cabinet', label: 'Cabinet' },
      { value: 'visite', label: 'Visite' },
      { value: 'degrade', label: 'Dégradé' },
      { value: 'papier', label: 'Papier' },
    ];

    private subs = new Subscription();
    private readonly destroy$ = new Subject<void>();
    private soinsEditedManually = false;
    public ListProfils: Array<{ nom: string; prenom: string; id: string }> = [];

    constructor(
      private fb: FormBuilder,
      private recettes: RecettesService,
      private snackBar: SnackbarService,
      private dialogRef: MatDialogRef<AddFactureHandComponent>,
      @Inject(MAT_DIALOG_DATA) public data: AddFactureHandData,
      private patientSrv: PatientService,
      private _perimeterSrv: PerimetersService,
      private _resourceSrv: RessourceService,
    ) {}

    ngOnInit(): void {
      this.initForm();
    }

    ngOnDestroy(): void {
      this.subs.unsubscribe();
      this.destroy$.next();
      this.destroy$.complete();
    }

    public breakLinkPatient(){
      this.patientSelected = null;
      this.form.patchValue({
        idPatient: null,
        searchPatient: null,
      }, { emitEvent: false });
      this.patientId = null;
      this.SearchPatientNom = null;
      if (this.autoTrigger) this.autoTrigger.closePanel();
    }

    public initForm(){
      this.form = this.fb.group({
        // Patient
        searchPatient: [''],
        idPatient: [null, [Validators.required]],

        // Profil émetteur
        idProfil: [null, [Validators.required]],

        // Dates
        dateFacture: [new Date(), [Validators.required]],
        dateSoins: [new Date()],

        // Infos facture
        typeFse: ['cabinet', [Validators.required]],
        libelleFacture: ['', [Validators.required]],
        numFactureLibre: [''],

        // Montants
        montantTotal: [0, [Validators.required, this.minStrict(0.01)]],
        montantAMO: [0, [Validators.min(0)]],
        montantAMC: [0, [Validators.min(0)]],
        montantPatient: [0, [Validators.min(0)]],
      });

      // Wire autocomplete si service dispo
      const searchCtl = this.form.get('searchPatient');
      if (searchCtl) {
        this.subs.add(
          searchCtl!.valueChanges.pipe(
                filter((v: any): v is string => typeof v === 'string'),
                map(v => v.trim()),
                debounceTime(500),distinctUntilChanged(),
                tap(q => { this.loading = q.length >= 3; }),
                switchMap(q => {
                  if (q.length < 3) {
                    this.loading = false;
                    this.patientId = null; // si tu veux vider la sélection
                    return of([]);
                  }
                  const words = q.split(/\s+/).join(' ');
                  return from(
                    this.patientSrv.patientsInlineSearch(words, {
                      perpage: -1,
                      currentPage: 1,
                      sortOrder: SortEnum.ASC,
                      sortBy: 'nom'
                    })
                  ).pipe(
                    catchError(() => of([]))
                  );
                }),
                tap(() => { this.loading = false; })
            ).subscribe((results:Paginated<ResultatPatient>) => {
              // console.log('Résultats patients:', results);
              this.patientsList = results?.objects || []; // affecter à ta liste pour affichage/autocomplete
              if (this.patientsList.length && this.autoTrigger && !this.autoTrigger.panelOpen) {
                this.autoTrigger.openPanel();
              }
          })
        );
      }

      const toNum = (v: any) => {
        if (v === null || v === undefined || v === '') return 0;
        const n = parseFloat(String(v).replace(',', '.'));
        return Number.isFinite(n) ? n : 0;
      };
      // Calcul montant total
      const amoCtrl = this.form.get('montantAMO');
      const amcCtrl = this.form.get('montantAMC');
      const patCtrl = this.form.get('montantPatient');
      const totCtrl = this.form.get('montantTotal');
      combineLatest([
        amoCtrl!.valueChanges.pipe(startWith(amoCtrl!.value)),
        amcCtrl!.valueChanges.pipe(startWith(amcCtrl!.value)),
        patCtrl!.valueChanges.pipe(startWith(patCtrl!.value)),
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([amo, amc, pat]) => {
        const total = +(toNum(amo) + toNum(amc) + toNum(pat)).toFixed(2);
        if (totCtrl!.value !== total) {
          // évite la boucle infinie
          totCtrl!.patchValue(total, { emitEvent: false });
        }
      });

      //listProfil
      if(this.data.profils){ //fournis par l'appel
        this.ListProfils = this.data.profils;
        this.form.get('idProfil')?.setValue(this.data.defaultProfilId);
      }

      const df = this.form.get('dateFacture')!;
      const ds = this.form.get('dateSoins')!;

      ds.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => { if (ds.dirty) this.soinsEditedManually = true; });

      df.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(v => {
          const next = v instanceof Date ? v : (v ? new Date(v) : null);
          if (!ds.value || !this.soinsEditedManually) {
            ds.setValue(next, { emitEvent: false });
            ds.markAsPristine();
          }
      });
    }

    public onPatientSelected(p: any) {
      if(p.id){
        this.patientSelected = p;
        this.form.patchValue({
          idPatient: p?.id || null,
          searchPatient: this.displayPatient(p),
        }, { emitEvent: false });
        this.patientId = p?.id || null;
        this.SearchPatientNom = this.displayPatient(p);
      }
      // Referme le panneau
      if (this.autoTrigger) this.autoTrigger.closePanel();
    }

    // Soumission
    public async onCreate() {
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }
      const v = this.form.value;

      const payload: FactureCreate = {
        typeFSE: v.typeFse,
        idPatient: v.idPatient,
        idProfil: v.idProfil,
        dateFacture: this.toYMD(v.dateFacture),
        dateSoins: v.dateSoins ? this.toYMD(v.dateSoins) : undefined,
        libelleFacture: v.libelleFacture || undefined,
        montantTotal: Number(v.montantTotal),
        montantAMO: this.toOptNumber(v.montantAMO),
        montantAMC: this.toOptNumber(v.montantAMC),
        montantPatient: this.toOptNumber(v.montantPatient),
        origine: 'manuel',
      };

      try {
        const id = await this.recettes.create(payload);
        if (id) {
          this.snackBar.openSnackBarSuccess('Facture créée.');
          this.dialogRef.close({ created: true, id, data: payload });
        } else {
          // Cas 204 (import/batch) si jamais
          this.dialogRef.close({ created: true, data: payload });
        }
      } catch (e) {
        // Snackbar déjà gérée dans le service en général
      }
    }

    public close() {
      this.dialogRef.close({ created: false });
    }

    // ===== Utils =====
    // Option robuste
    private toYMD(d: any): string | null {
      if (!d) return null;

      // Déjà une string → normalise vite fait
      if (typeof d === 'string') {
        // YYYY-MM-DD (garde les 10 premiers)
        const iso = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
        // DD/MM/YYYY → YYYY-MM-DD
        const fr = d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (fr) return `${fr[3]}-${fr[2]}-${fr[1]}`;
        // sinon on fait confiance
        return d;
      }

      // Moment (ou dayjs-like) : possède format()/isValid()
      if (d && typeof d.format === 'function' && typeof d.isValid === 'function') {
        return d.isValid() ? d.format('YYYY-MM-DD') : null;
      }

      // Timestamp
      if (typeof d === 'number') {
        const dt = new Date(d);
        if (!isNaN(dt.getTime())) return ymd(dt);
        return null;
      }

      // Date native
      if (d instanceof Date) {
        if (!isNaN(d.getTime())) return ymd(d);
        return null;
      }

      // Dernier essai : constructible en Date
      const parsed = new Date(d);
      return isNaN(parsed.getTime()) ? null : ymd(parsed);

      function ymd(dt: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
      }
    }
    private toOptNumber(n: any): number | undefined {
      if (n === null || n === undefined || n === '') return undefined;
      const num = Number(n);
      return isNaN(num) ? undefined : num;
    }
    private minStrict(min: number) {
      return (c: AbstractControl) => {
        const val = Number(c.value);
        if (isNaN(val)) return { min: true };
        return val > min ? null : { min: true };
      };
    }

    // Affichage Autocomplete
    public displayPatient = (p?: any): string => {
      if (!p) return '';
      // essaie différentes propriétés usuelles vues dans tes bouts de code
      return (
        p.nomPrenomDateNaissance ||
        p.display ||
        [p.nomUsage || p.nom || '', p.prenomUsage || p.prenom || ''].filter(Boolean).join(' ')
      );
    };
}

